import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Search, Download, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { toast } from 'sonner';

export default function UsuariosRegistrados() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 50,
    total_pages: 0
  });
  const [filters, setFilters] = useState({
    fecha_inicio: '',
    fecha_final: '',
    documento: ''
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchUsers();
  }, [pagination.page, pagination.limit]);

  const fetchUsers = async (resetPage = false) => {
    try {
      setLoading(true);
      const page = resetPage ? 1 : pagination.page;
      
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', pagination.limit.toString());
      
      if (filters.fecha_inicio) params.append('fecha_inicio', filters.fecha_inicio);
      if (filters.fecha_final) params.append('fecha_final', filters.fecha_final);
      if (filters.documento) params.append('documento', filters.documento);

      const response = await fetch(`${API_URL}/api/users/registered?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Error al cargar usuarios');
      }

      const data = await response.json();
      setUsers(data.users);
      setPagination(prev => ({
        ...prev,
        total: data.total,
        page: data.page,
        total_pages: data.total_pages
      }));
    } catch (error) {
      toast.error('Error al cargar los usuarios registrados');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers(true);
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      
      const params = new URLSearchParams();
      if (filters.fecha_inicio) params.append('fecha_inicio', filters.fecha_inicio);
      if (filters.fecha_final) params.append('fecha_final', filters.fecha_final);
      if (filters.documento) params.append('documento', filters.documento);

      const response = await fetch(`${API_URL}/api/users/export-excel?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Error al exportar');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const filename = `usuarios_registrados_${new Date().toISOString().split('T')[0]}.xlsx`;
      
      // Force download
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      toast.success('Archivo descargado: ' + filename);
    } catch (error) {
      toast.error('Error al exportar el reporte');
      console.error(error);
    } finally {
      setExporting(false);
    }
  };

  const goToPage = (page) => {
    if (page >= 1 && page <= pagination.total_pages) {
      setPagination(prev => ({ ...prev, page }));
    }
  };

  const getDisplayName = (user) => {
    if (user.user_type === 'natural') {
      return `${user.first_name || ''} ${user.second_name || ''} ${user.first_lastname || ''} ${user.second_lastname || ''}`.trim();
    }
    return user.company_name || '';
  };

  const getDocumentId = (user) => {
    if (user.user_type === 'natural') {
      return user.document_id || '';
    }
    return user.nit || '';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('es-CO', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  // Calculate showing range
  const startRecord = pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1;
  const endRecord = Math.min(pagination.page * pagination.limit, pagination.total);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-[#0F172A] border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_login-verify-system/artifacts/gyieqwft_Captura%20de%20Pantalla%202025-12-10%20a%20la%28s%29%207.03.41%20a.%C2%A0m..png" 
              alt="Logo" 
              className="h-12 w-auto object-contain brightness-0 invert"
            />
            <div className="border-l border-slate-600 pl-4">
              <h1 className="text-xl font-semibold text-white">
                Plataforma de Registro Único
              </h1>
            </div>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Volver al Panel</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search Section */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 mb-6">
          <p className="text-blue-700 font-medium mb-4">
            Por favor seleccione un rango de fechas de registro o un número de documento o NIT
          </p>
          <div className="border-b-4 border-[#0F172A] mb-6"></div>
          
          <form onSubmit={handleSearch} className="flex flex-wrap items-end gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Fecha Inicio</label>
              <input
                type="date"
                value={filters.fecha_inicio}
                onChange={(e) => setFilters(prev => ({ ...prev, fecha_inicio: e.target.value }))}
                className="px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Fecha Final</label>
              <input
                type="date"
                value={filters.fecha_final}
                onChange={(e) => setFilters(prev => ({ ...prev, fecha_final: e.target.value }))}
                className="px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">NIT o Documento:</label>
              <input
                type="text"
                value={filters.documento}
                onChange={(e) => setFilters(prev => ({ ...prev, documento: e.target.value }))}
                placeholder="Buscar..."
                className="px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <Search className="h-4 w-4" />
            </button>
          </form>
        </div>

        {/* Table Section */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
          {/* Export Button */}
          <div className="p-4 border-b border-slate-200 flex justify-end">
            <button
              onClick={handleExport}
              disabled={exporting || pagination.total === 0}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="h-4 w-4" />
              {exporting ? 'Exportando...' : 'Descargar Excel'}
            </button>
          </div>

          {/* Table */}
          {loading ? (
            <div className="p-8 text-center text-slate-500">Cargando...</div>
          ) : users.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              No hay usuarios registrados
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#0F172A] text-white">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Tipo Usuario</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Documento/NIT</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Nombre / Razón Social</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Email</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Teléfono</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Verificado</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Fecha Registro</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm text-slate-700">
                        {user.user_type === 'natural' ? 'Persona Natural' : 'Persona Jurídica'}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700 font-medium">
                        {getDocumentId(user)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-900">
                        {getDisplayName(user)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {user.email}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {user.phone || '-'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {user.verified ? (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                            Sí
                          </span>
                        ) : (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                            No
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {formatDate(user.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-between bg-slate-50">
            <div className="text-sm text-slate-600">
              Mostrando {startRecord} a {endRecord} de {pagination.total} registros
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => goToPage(1)}
                disabled={pagination.page === 1}
                className="p-1 rounded hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Primera página"
              >
                <ChevronsLeft className="h-5 w-5" />
              </button>
              <button
                onClick={() => goToPage(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="p-1 rounded hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Página anterior"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <span className="px-3 py-1 text-sm">
                Página {pagination.page} de {pagination.total_pages || 1}
              </span>
              <button
                onClick={() => goToPage(pagination.page + 1)}
                disabled={pagination.page >= pagination.total_pages}
                className="p-1 rounded hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Página siguiente"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
              <button
                onClick={() => goToPage(pagination.total_pages)}
                disabled={pagination.page >= pagination.total_pages}
                className="p-1 rounded hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Última página"
              >
                <ChevronsRight className="h-5 w-5" />
              </button>
              <select
                value={pagination.limit}
                onChange={(e) => {
                  const newLimit = parseInt(e.target.value);
                  setPagination(prev => ({ ...prev, limit: newLimit, page: 1 }));
                }}
                className="ml-2 px-2 py-1 border border-slate-300 rounded text-sm"
                data-testid="page-size-selector"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value={500}>500</option>
                <option value={1000}>1000</option>
              </select>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
